<template>
  <!-- Create Payment Action Button -->
  <v-row class="pa-3 mt-1 pr-0 d-flex justify-end">
    <v-col class="text-end">
      <v-btn color="primary" prepend-icon="mdi-plus" @click="createPayment()">
        {{ $t("common.action.createPayment") }}
      </v-btn>
    </v-col>
  </v-row>

  <!-- Recent Payments Card -->
  <v-card class="mb-0 pb-0">
    <v-row align-lg="center" class="ml-8 mt-3 mb-2">
      <v-icon class="mdi-36px" align="center">mdi-card-bulleted-outline</v-icon>
      <v-card-title class="text-body-1 font-weight-medium">{{
        $t("page.dashboard.recentPayments")
      }}</v-card-title>
    </v-row>
    <base-stripe-data-table
      :headers="headers"
      fetchAction="fetchPayments"
      itemsGetter="payments"
      storeNamespace="payment"
      skip-initial-fetch-when-items-exist
    >
      <template v-slot:item="{ item }">
        <tr :key="asRow(item).id" @click="goTo(asRow(item))" style="cursor: pointer">
          <td class="text-center">
            <p v-if="getPaymentDisplayAmount(asRow(item)) === undefined" class="pr-6">
              {{ $t("page.paymentDetails.authorization.unavailable") }}
            </p>
            <p v-else class="pr-6" v-money="{ amount: getPaymentDisplayAmount(asRow(item)) }" />
          </td>
          <td class="text-center">
            <status-chip
              :status="usePaymentStore().getPaymentStatus(asRow(item)) ?? undefined"
              :capture-before="getPaymentCaptureBefore(asRow(item))"
              :partiallyRefundAmount="getPartiallyRefundAmount(asRow(item))"
              :errorMessage="getErrorMessage(asRow(item)) ?? undefined"
              :payment-id="asRow(item).id"
              :intent-status="asRow(item).status ?? undefined"
              :cart="asRow(item).metadata?.isCart === 'true'"
            />
          </td>
          <td class="text-center">
            <p
              v-if="!isPaymentLink(asRow(item))"
              class="pr-8 text-grey"
              v-dash="asRow(item).description ?? ''"
            />
            <p v-else class="pr-8 text-grey" v-dash="paymentLinkName(asRow(item)) ?? ''" />
          </td>
          <td class="text-center">
            <p class="text-medium-emphasis" v-dateWithTime="asRow(item).created" />
          </td>
          <td class="text-center">
            <div class="pr-6">
              {{
                isPaymentLink(asRow(item))
                  ? $t("common.action.paymentLink")
                  : entityName(asRow(item).location)
              }}
              <v-tooltip :text="getHintMessage(asRow(item))" location="top">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    size="xs"
                    color="grey"
                    class="pl-3"
                    v-if="deletedCheck(asRow(item))"
                    >mdi-information</v-icon
                  >
                </template></v-tooltip
              >
              <div class="text-medium-emphasis text-body-2">
                {{
                  isPaymentLink(asRow(item))
                    ? formatString(asRow(item).paymentLink?.id ?? "")
                    : entityName(asRow(item).terminal)
                }}
              </div>
            </div>
          </td>
          <td class="text-center">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon flat>
                  <v-icon color="grey">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, index) in actionList(asRow(item))"
                  :key="index"
                  :value="index"
                  @click.stop="action.action(asRow(item).id)"
                >
                  <v-list-item-title>{{ action.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </base-stripe-data-table>
  </v-card>
  <refund-dialog ref="refundDialog" />
</template>

<script lang="ts" setup>
import { usePaymentStore } from "@/payments/stores/payments";
import { ref } from "vue";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import RefundDialog from "@/shared/components/Common/RefundDialog.vue";
import i18n from "@/app/plugins/i18n";
import { useRouter } from "vue-router";
import { PaymentStatus } from "@/shared/enums/status.enum";
import BaseStripeDataTable from "@/shared/components/Common/BaseStripeDataTable.vue";
import { useAuthStore } from "@/auth/stores/auth";
import { formatString } from "@/shared/utils/firstLast4.utils";
import type { Payment } from "@/payments/types/payments";
import type { DataTableHeader } from "vuetify";
import {
  getPaymentCaptureBefore,
  getPaymentDisplayAmount,
} from "@/payments/utils/paymentStatus.utils";

const asRow = (item: { id?: string }): Payment => item as Payment;

const refundDialog = ref<null | { open: (id?: string) => null }>(null);
const router = useRouter();
const isPaymentLink = (payment: Payment) => {
  return payment.metadata?.payment_link !== undefined;
};
const paymentLinkName = (payment: Payment) => {
  if (payment.paymentLink?.name) {
    return formatString(payment.paymentLink.name);
  }
};

const getErrorMessage = (payment: Payment) => {
  const lastPaymentError = payment?.last_payment_error;
  if (lastPaymentError && typeof lastPaymentError === "object" && lastPaymentError.code) {
    return lastPaymentError.code.replace(/_/g, " ");
  }
  return null;
};

const headers: DataTableHeader[] = [
  {
    title: i18n.global.t("page.payment.amount"),
    align: "center",
    key: "amount",
  },

  {
    title: i18n.global.t("common.action.status"),
    align: "center",
    key: "status",
  },
  {
    title: i18n.global.t("common.action.description"),
    align: "center",
    key: "description",
  },
  {
    title: i18n.global.t("page.dashboard.payments.date"),
    align: "center",
    key: "created",
  },
  {
    title: i18n.global.t("common.action.source"),
    align: "center",
    key: "terminal",
  },
  {
    title: "",
    align: "end",
    key: "menu",
    sortable: false,
  },
];

const actionListFailed = [
  {
    title: i18n.global.t("page.dashboard.payments.sendToReader"),
    action: (id: string) => sendToReader(id),
  },
];

const actionViewDetails = [
  {
    title: i18n.global.t("page.dashboard.payments.viewDetails"),
    action: (id: string) => viewDetails(id),
  },
];

const actionListPaid = !useAuthStore().isStaff
  ? [
      {
        title: i18n.global.t("page.dashboard.payments.refund"),
        action: (id: string) => refundDialog.value?.open(id),
      },
      {
        title: i18n.global.t("page.dashboard.payments.viewDetails"),
        action: (id: string) => viewDetails(id),
      },
    ]
  : actionViewDetails;

const actionList = (payment: Payment) => {
  const paymentStatus = usePaymentStore().getPaymentStatus(payment);
  if (
    paymentStatus &&
    [PaymentStatus.REFUNDED, PaymentStatus.CANCELED].includes(paymentStatus as PaymentStatus)
  ) {
    return actionViewDetails;
  } else {
    return payment.status === PaymentStatus.SUCCEEDED ? actionListPaid : actionListFailed;
  }
};

const getPartiallyRefundAmount = (payment: Payment) => {
  const latestCharge = payment?.latest_charge;

  if (
    latestCharge &&
    typeof latestCharge === "object" &&
    typeof latestCharge.amount_refunded === "number" &&
    latestCharge.amount_refunded > 0 &&
    latestCharge.amount_refunded < payment.amount
  ) {
    return latestCharge.amount_refunded / 100;
  }

  return 0;
};

const entityName = (entity: Payment["location"]): string | undefined =>
  typeof entity === "object" ? entity?.name : undefined;

const isDeletedEntity = (entity: Payment["location"]): boolean =>
  typeof entity === "object" && entity?.deleted === true;

const getHintMessage = (payment: Payment) => {
  const bothDeleted = isDeletedEntity(payment.location) && isDeletedEntity(payment.terminal);
  if (bothDeleted) {
    return i18n.global.t("page.payment.deletedHint.locationAndReader");
  } else if (isDeletedEntity(payment.terminal)) {
    return i18n.global.t("page.payment.deletedHint.reader");
  } else if (isDeletedEntity(payment.location)) {
    return i18n.global.t("page.payment.deletedHint.location");
  }
};

const deletedCheck = (payment: Payment) => {
  return isDeletedEntity(payment.terminal) || isDeletedEntity(payment.location);
};

const sendToReader = (id?: string) => {
  const routeParams = id ? { name: "processPayment", params: { id } } : { name: "createPayment" };

  router.push(routeParams);
};

const createPayment = () => {
  router.push({ name: "createPayment" });
};

const viewDetails = (id: string) => {
  router.push({ path: `/payments/details/${id}` });
};

const goTo = (payment: Payment) => {
  if (
    payment.status &&
    [PaymentStatus.SUCCEEDED, PaymentStatus.CANCELED, PaymentStatus.REQUIRES_CAPTURE].includes(
      payment.status as PaymentStatus,
    )
  ) {
    viewDetails(payment.id);
  } else {
    sendToReader(payment.id);
  }
};
</script>
